import type { Playbook, Offer } from "../types";

const offer = (o: Omit<Offer, "sendsPrompt"> & Partial<Pick<Offer, "sendsPrompt">>): Offer => o;

/**
 * Vault publishes one of two snapshots (see `useVaultPageContext`):
 * an open file, or the folder being browsed plus how many items are selected.
 *
 * Priorities are ordered by how often the offer is the thing somebody actually
 * wants: reading a file you just opened beats sharing it, and both beat tidying
 * a folder, which is a deliberate act somebody is already part-way through.
 */
export const drivePlaybooks: Playbook[] = [
  {
    app: "drive",
    priority: 70,
    match: (s) =>
      s.surface === "file" && s.item_id
        ? offer({
            key: "drive.file.summarize",
            app: "drive",
            titleKey: "assistant.mochi.offers.drive.summarize.title",
            bodyKey: "assistant.mochi.offers.drive.summarize.body",
            ctaKey: "assistant.mochi.offers.drive.summarize.cta",
            promptKey: "assistant.mochi.offers.drive.summarize.prompt",
            promptVars: { item_id: s.item_id },
          })
        : null,
  },
  {
    app: "drive",
    priority: 50,
    match: (s) =>
      s.surface === "file" && s.item_id
        ? offer({
            key: "drive.file.share",
            app: "drive",
            titleKey: "assistant.mochi.offers.drive.share.title",
            bodyKey: "assistant.mochi.offers.drive.share.body",
            ctaKey: "assistant.mochi.offers.drive.share.cta",
            promptKey: "assistant.mochi.offers.drive.share.prompt",
            promptVars: { item_id: s.item_id },
          })
        : null,
  },
  {
    app: "drive",
    priority: 40,
    // Two is the threshold, not one: organizing a single file is not a job
    // anybody needs help with, and offering it there would be noise on every
    // ordinary click. `selected_count` is absent on a thin snapshot, which
    // reads as nothing selected rather than as a match.
    match: (s) => {
      const selected = s.facts?.selected_count;
      return s.surface === "folder" && typeof selected === "number" && selected > 1
        ? offer({
            key: "drive.folder.organize",
            app: "drive",
            titleKey: "assistant.mochi.offers.drive.organize.title",
            bodyKey: "assistant.mochi.offers.drive.organize.body",
            ctaKey: "assistant.mochi.offers.drive.organize.cta",
            promptKey: "assistant.mochi.offers.drive.organize.prompt",
            promptVars: { selected_count: selected },
          })
        : null;
    },
  },
];
